// External Dependencies
import { alpha } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { useCallback, useState } from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CardActions from '@mui/material/CardActions';
import CardHeader from '@mui/material/CardHeader';
import Collapse from '@mui/material/Collapse';
import Container from '@mui/material/Container';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import styled from 'styled-components';
import ShieldAlertIcon from 'mdi-material-ui/ShieldAlert';

// Internal Dependencies
import { isMobileScreenSize } from 'state/selectors';

// Local Dependencies
import DangerZoneItem, { DangerZoneItemProps } from './DangerZoneItem';
import EnhancedCard from '../EnhancedCard';

// Local Typings
interface Props {
  items: DangerZoneItemProps[];
}

// Local Variables
const StyledContainer = styled(Container)(({ theme }) => ({
  '.danger-zone-avatar': {
    backgroundColor: theme.palette.error.main,
  },
  '.danger-zone-card': {
    borderColor: theme.palette.dangerZone,
    borderRadius: 12,
    borderWidth: 1.5,
  },
  '.danger-zone-header': {
    backgroundColor: alpha(theme.palette.dangerZone, 0.08),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  '.danger-zone-title': {
    fontSize: '1.1rem',
    fontWeight: 500,
  },
}));

// Component Definition
const DangerZone = ({
  items,
}: Props): JSX.Element => {
  const isMobileWidth = useSelector(isMobileScreenSize);

  const [showDangerZoneContent, setShowDangerZoneContent] = useState(false);

  const handlePressExpand = useCallback(() => {
    setShowDangerZoneContent((prev) => !prev);
  }, []);

  return (
    <StyledContainer maxWidth="sm">
      <EnhancedCard className="danger-zone-card">
        <CardHeader
          avatar={(
            <Avatar className="danger-zone-avatar">
              <ShieldAlertIcon />
            </Avatar>
          )}
          className="danger-zone-header"
          title={(
            <Typography
              className="danger-zone-title"
              variant="h6"
            >
              Danger Zone
            </Typography>
          )}
        />
        <Collapse in={!showDangerZoneContent}>
          <CardActions>
            <Button
              color="error"
              onClick={handlePressExpand}
              size="small"
              variant="text"
            >
              Expand
            </Button>
          </CardActions>
        </Collapse>

        <Collapse in={showDangerZoneContent}>
          <List dense={isMobileWidth}>
            {items.map((item) => (
              <DangerZoneItem
                isMobileWidth={isMobileWidth}
                key={item.title}
                {...item}
              />
            ))}
          </List>
        </Collapse>

      </EnhancedCard>
    </StyledContainer>
  );
};

export type { DangerZoneItemProps } from './DangerZoneItem';

export default DangerZone;
